import React, { useState, useEffect } from 'react';
import { formatNumber } from '../../helpers/Utils';
import { cn } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const StatCard = ({ title, value, icon: Icon, color, trendData, className, formatCurrency = false, invertTrend = false, breakdownData = null, activePercentage = null, trendLabel = null }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    
    // Debug: Log breakdownData when tooltip is shown
    useEffect(() => {
        if (showTooltip && breakdownData) {
            console.log('StatCard breakdownData for', title, ':', breakdownData);
        }
    }, [showTooltip, breakdownData, title]);
    
    // Format value based on type
    const formattedValue = formatCurrency 
        ? `AED ${formatNumber(value)}`
        : formatNumber(value);

    // Extract trend percentage from trendData
    const trendPercentage = trendData?.percentage ?? null;
    
    // Determine trend direction and color
    const hasTrend = trendPercentage !== null && trendPercentage !== undefined;
    const isPositive = hasTrend ? trendPercentage >= 0 : null;
    const shouldShowPositive = hasTrend && invertTrend ? !isPositive : isPositive;
    const trendColor = hasTrend && shouldShowPositive ? '#16a34a' : (hasTrend ? '#dc2626' : '#6b7280');
    const trendBgColor = hasTrend && shouldShowPositive ? 'rgba(22, 163, 74, 0.1)' : (hasTrend ? 'rgba(220, 38, 38, 0.1)' : 'transparent');
    const trendArrow = hasTrend && isPositive ? '↑' : (hasTrend ? '↓' : '');

    // Format value for tooltip
    const formatTooltipValue = (val) => {
        if (formatCurrency) {
            return `AED ${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        return val.toLocaleString();
    };

    // Generic items renderer for tooltip breakdowns. Used when callers pass
    // breakdownData.currentItems / previousItems as an array of
    // { label, value, color?, formatAsPercent? }. Keeps the existing
    // hardcoded shapes (canceled/rejected, online/offline, total/active)
    // working unchanged below.
    const renderBreakdownItems = (items) => {
        if (!Array.isArray(items) || items.length === 0) return null;
        return (
            <div style={{
                fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem',
                paddingTop: '0.25rem', borderTop: '1px solid #f3f4f6',
            }}>
                {items.map((item) => (
                    <div key={item.label} style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: '0.125rem',
                    }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                            {item.color && (
                                <span style={{
                                    width: '0.5rem', height: '0.5rem', borderRadius: '50%',
                                    backgroundColor: item.color, display: 'inline-block',
                                }} />
                            )}
                            {item.label}:
                        </span>
                        <span style={{ fontWeight: 500 }}>
                            {item.formatAsPercent
                                ? `${Number(item.value || 0).toFixed(2)}%`
                                : formatTooltipValue(Number(item.value) || 0)}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    const hasItemsCurrent = Array.isArray(breakdownData?.currentItems) && breakdownData.currentItems.length > 0;
    const hasItemsPrevious = Array.isArray(breakdownData?.previousItems) && breakdownData.previousItems.length > 0;

    return (
        <Card
            className={cn('relative overflow-visible', className)}
            style={{
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                border: '1px solid #e5e7eb',
                borderRadius: '0.75rem',
                background: '#ffffff',
                position: 'relative',
                height: '100%',
                zIndex: showTooltip ? 2000 : 'auto'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '';
                e.currentTarget.style.transform = 'translateY(0)';
            }}
        >
            {/* Colored accent bar */}
            <div style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                height: '100%',
                left: 0,
                width: '6px',
                backgroundColor: color || '#3b82f6',
                borderRadius: '0.75rem 0 0 0.75rem',
                zIndex: 1,
                pointerEvents: 'none'
            }} />

            <CardHeader
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 1.5rem',
                    // paddingTop: '1.25rem',
                    // paddingLeft: '1.5rem',
                    position: 'relative',
                    zIndex: 2
                }}
            >
                <CardTitle style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: 600, 
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    lineHeight: '1.2',
                    margin: 0,
                    flex: 1,
                    minWidth: 0
                }}>
                    {title}
                </CardTitle>
                {Icon && (
                    <div style={{
                        height: '44px',
                        width: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '0.5rem',
                        backgroundColor: color ? `${color}15` : 'rgba(59, 130, 246, 0.1)',
                        color: color || '#3b82f6',
                        flexShrink: 0,
                        marginLeft: '0.75rem'
                    }}>
                        <Icon size={20} style={{ color: color || '#3b82f6' }} />
                    </div>
                )}
            </CardHeader>
            <CardContent style={{ 
                padding: '0 1.5rem 0 1.5rem',
                flex: 1,
                // display: 'flex',
                // flexDirection: 'column',
                // justifyContent: 'space-between',
                // position: 'relative',
                zIndex: 2
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    minHeight: '3rem'
                }}>
                    <div style={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: color || '#1f2937',
                        lineHeight: '2.5rem',
                        letterSpacing: '-0.03em',
                        flex: 1,
                        minWidth: 0,
                        wordBreak: 'break-word'
                    }}>
                        {formattedValue}
                    </div>
                    {hasTrend && trendData && (
                        <div 
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: trendColor,
                                padding: '0.375rem 0.625rem',
                                backgroundColor: trendBgColor,
                                borderRadius: '0.5rem',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                position: 'relative',
                                flexShrink: 0,
                                alignSelf: 'flex-start',
                                marginTop: '0.125rem',
                                zIndex: showTooltip ? 2001 : 'auto'
                            }}
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                        >
                            <span>{trendArrow}</span>
                            <span>{Math.abs(trendPercentage).toFixed(1)}%</span>
                            {showTooltip && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginBottom: '0.5rem',
                                    padding: '0.75rem',
                                    backgroundColor: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.5rem',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                    zIndex: 10000,
                                    minWidth: '280px',
                                    pointerEvents: 'auto'
                                }}>
                                    <div style={{ marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                                            Current Period
                                        </div>
                                        {breakdownData ? (
                                            <>
                                                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.125rem' }}>
                                                    {formatTooltipValue(trendData.currentTotal)}
                                                </div>
                                                {hasItemsCurrent ? renderBreakdownItems(breakdownData.currentItems) : (((breakdownData.currentCanceled !== undefined && breakdownData.currentCanceled !== null) &&
                                                  (breakdownData.currentRejected !== undefined && breakdownData.currentRejected !== null)) ||
                                                 ((breakdownData.currentOnline !== undefined && breakdownData.currentOnline !== null) &&
                                                  (breakdownData.currentOffline !== undefined && breakdownData.currentOffline !== null)) ||
                                                 ((breakdownData.currentTotal !== undefined && breakdownData.currentTotal !== null) &&
                                                  (breakdownData.currentActive !== undefined && breakdownData.currentActive !== null)) ? (
                                                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem', paddingTop: '0.25rem', borderTop: '1px solid #f3f4f6' }}>
                                                        {(breakdownData.currentCanceled !== undefined && breakdownData.currentCanceled !== null) && 
                                                         (breakdownData.currentRejected !== undefined && breakdownData.currentRejected !== null) ? (
                                                            <>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.125rem' }}>
                                                                    <span>Canceled:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.currentCanceled)}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Rejected:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.currentRejected)}</span>
                                                                </div>
                                                            </>
                                                        ) : (breakdownData.currentOnline !== undefined && breakdownData.currentOnline !== null) && 
                                                             (breakdownData.currentOffline !== undefined && breakdownData.currentOffline !== null) ? (
                                                            <>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.125rem' }}>
                                                                    <span>Online:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.currentOnline)}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Offline:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.currentOffline)}</span>
                                                                </div>
                                                            </>
                                                        ) : (breakdownData.currentTotal !== undefined && breakdownData.currentTotal !== null) && 
                                                             (breakdownData.currentActive !== undefined && breakdownData.currentActive !== null) ? (
                                                            <>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.125rem' }}>
                                                                    <span>Total:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.currentTotal)}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Active:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.currentActive)}</span>
                                                                </div>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                ) : null)}
                                            </>
                                        ) : (
                                            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.125rem' }}>
                                                {formatTooltipValue(trendData.currentTotal)}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                                            {trendData.currentPeriod}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                                            Previous Period
                                        </div>
                                        {breakdownData ? (
                                            <>
                                                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.125rem' }}>
                                                    {formatTooltipValue(trendData.previousTotal)}
                                                </div>
                                                {hasItemsPrevious ? renderBreakdownItems(breakdownData.previousItems) : (((breakdownData.previousCanceled !== undefined && breakdownData.previousCanceled !== null) &&
                                                  (breakdownData.previousRejected !== undefined && breakdownData.previousRejected !== null)) ||
                                                 ((breakdownData.previousOnline !== undefined && breakdownData.previousOnline !== null) &&
                                                  (breakdownData.previousOffline !== undefined && breakdownData.previousOffline !== null)) ||
                                                 ((breakdownData.previousTotal !== undefined && breakdownData.previousTotal !== null) &&
                                                  (breakdownData.previousActive !== undefined && breakdownData.previousActive !== null)) ? (
                                                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem', paddingTop: '0.25rem', borderTop: '1px solid #f3f4f6' }}>
                                                        {(breakdownData.previousCanceled !== undefined && breakdownData.previousCanceled !== null) && 
                                                         (breakdownData.previousRejected !== undefined && breakdownData.previousRejected !== null) ? (
                                                            <>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.125rem' }}>
                                                                    <span>Canceled:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.previousCanceled)}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Rejected:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.previousRejected)}</span>
                                                                </div>
                                                            </>
                                                        ) : (breakdownData.previousOnline !== undefined && breakdownData.previousOnline !== null) && 
                                                             (breakdownData.previousOffline !== undefined && breakdownData.previousOffline !== null) ? (
                                                            <>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.125rem' }}>
                                                                    <span>Online:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.previousOnline)}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Offline:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.previousOffline)}</span>
                                                                </div>
                                                            </>
                                                        ) : (breakdownData.previousTotal !== undefined && breakdownData.previousTotal !== null) && 
                                                             (breakdownData.previousActive !== undefined && breakdownData.previousActive !== null) ? (
                                                            <>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.125rem' }}>
                                                                    <span>Total:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.previousTotal)}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                    <span>Active:</span>
                                                                    <span style={{ fontWeight: 500 }}>{formatTooltipValue(breakdownData.previousActive)}</span>
                                                                </div>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                ) : null)}
                                            </>
                                        ) : (
                                            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.125rem' }}>
                                                {formatTooltipValue(trendData.previousTotal)}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                                            {trendData.previousPeriod}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {hasTrend && (
                    <p style={{
                        fontSize: '0.6875rem',
                        color: '#9ca3af',
                        margin: 0,
                        marginTop: '0.25rem',
                        lineHeight: '1rem',
                        fontWeight: 400
                    }}>
                        {trendLabel || 'vs previous period'}
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(StatCard, (prevProps, nextProps) => {
  return (
    prevProps.title === nextProps.title &&
    prevProps.value === nextProps.value &&
    prevProps.icon === nextProps.icon &&
    prevProps.color === nextProps.color &&
    prevProps.formatCurrency === nextProps.formatCurrency &&
    prevProps.trendData?.percentage === nextProps.trendData?.percentage &&
    prevProps.trendData?.currentTotal === nextProps.trendData?.currentTotal &&
    prevProps.trendData?.previousTotal === nextProps.trendData?.previousTotal &&
    prevProps.trendData?.currentPeriod === nextProps.trendData?.currentPeriod &&
    prevProps.trendData?.previousPeriod === nextProps.trendData?.previousPeriod &&
    prevProps.invertTrend === nextProps.invertTrend &&
    prevProps.breakdownData?.currentCanceled === nextProps.breakdownData?.currentCanceled &&
    prevProps.breakdownData?.currentRejected === nextProps.breakdownData?.currentRejected &&
    prevProps.breakdownData?.previousCanceled === nextProps.breakdownData?.previousCanceled &&
    prevProps.breakdownData?.previousRejected === nextProps.breakdownData?.previousRejected &&
    prevProps.breakdownData?.currentOnline === nextProps.breakdownData?.currentOnline &&
    prevProps.breakdownData?.currentOffline === nextProps.breakdownData?.currentOffline &&
    prevProps.breakdownData?.previousOnline === nextProps.breakdownData?.previousOnline &&
    prevProps.breakdownData?.previousOffline === nextProps.breakdownData?.previousOffline
  );
});
